import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { Badge, Card, ErrorNote, Spinner, Stat, Status } from "../components/ui";
import { credits, relative } from "../lib/format";

// The staff surface. Everything here crosses the tenant boundary, which is why
// it lives behind requirePlatformAdmin on the server and why that endpoint
// answers 404 rather than 403 to everyone else.

type Tab = "fleet" | "tenants" | "builds";

export default function Admin() {
  const [tab, setTab] = useState<Tab>("fleet");
  const { data: fleet, error } = useQuery({ queryKey: ["fleet"], queryFn: api.admin.fleet });
  const { data: tenants } = useQuery({
    queryKey: ["admin-tenants"],
    queryFn: () => api.admin.tenants({ limit: 50 }),
    enabled: tab === "tenants",
  });
  const { data: builds } = useQuery({
    queryKey: ["admin-builds"],
    queryFn: () => api.admin.builds({ limit: 50 }),
    enabled: tab === "builds",
  });

  if (error) return <ErrorNote error={error} />;
  if (!fleet) return <Spinner />;

  const rails: [string, boolean, string][] = [
    ["Fleet host", fleet.configured.fleet, "deploys are simulated without it"],
    ["Shared RDS", fleet.configured.rds, "apps fall back to SQLite without it"],
    ["Anthropic", fleet.configured.anthropic, "the deterministic planner runs without it"],
    ["Stripe", fleet.configured.stripe, "purchases are simulated without it"],
    ["SMTP", fleet.configured.smtp, "sign-in codes go to the log without it"],
    ["Build sandbox", fleet.configured.sandbox, "generated code runs with Atelier's own environment without it"],
  ];

  return (
    <div>
      <header className="mb-6">
        <h1 className="text-xl font-semibold tracking-tight">Admin</h1>
        <p className="mt-0.5 text-sm text-muted">Every tenant, every build, and the fleet.</p>
      </header>

      <nav className="mb-5 flex gap-1 border-b border-hairline">
        {(["fleet", "tenants", "builds"] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm capitalize transition ${
              tab === t ? "border-clay font-medium" : "border-transparent text-muted hover:text-ink"
            }`}
          >
            {t}
          </button>
        ))}
      </nav>

      {tab === "fleet" && (
        <div>
          <div className="mb-4 grid gap-3 sm:grid-cols-4">
            <Stat label="Apps deployed" value={fleet.ports_used} />
            <Stat label="Ports free" value={fleet.ports_free} sub={`of ${fleet.ports_total}`} />
            <Stat label="Build slots free" value={`${fleet.builds_free} / ${fleet.build_slots}`} />
            <Stat label="Failing apps" value={fleet.failing} />
          </div>

          <Card className="mb-4 p-5">
            <h2 className="mb-3 text-sm font-medium">Rails</h2>
            <ul className="grid gap-2 sm:grid-cols-2">
              {rails.map(([label, on, consequence]) => (
                <li key={label} className="flex items-start gap-2 text-sm">
                  <span
                    className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${on ? "bg-moss" : "bg-muted"}`}
                  />
                  <span>
                    {label}
                    {!on && <span className="block text-2xs text-muted">{consequence}</span>}
                  </span>
                </li>
              ))}
            </ul>
            <p className="mt-4 font-mono text-2xs text-muted">
              {fleet.host || "no fleet host"} · {fleet.apps_domain}
            </p>
          </Card>

          <Card className="overflow-hidden">
            <h2 className="border-b border-hairline px-5 py-3 text-sm font-medium">Apps on the fleet</h2>
            {fleet.apps.length === 0 ? (
              <p className="px-5 py-8 text-center text-sm text-muted">Nothing deployed yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-hairline text-left text-xs text-muted">
                    <tr>
                      <th className="px-5 py-2 font-medium">App</th>
                      <th className="px-5 py-2 font-medium">Port</th>
                      <th className="px-5 py-2 font-medium">Status</th>
                      <th className="px-5 py-2 font-medium">URL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fleet.apps.map((a) => (
                      <tr key={a.slug} className="border-b border-hairline/60 last:border-0">
                        <td className="px-5 py-2">
                          {a.project}
                          <span className="block font-mono text-2xs text-muted">{a.slug}</span>
                        </td>
                        <td className="px-5 py-2 tabular-nums">{a.port}</td>
                        <td className="px-5 py-2"><Status value={a.status} /></td>
                        <td className="truncate px-5 py-2 font-mono text-2xs text-muted">{a.url || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>
      )}

      {tab === "tenants" && (
        <Card className="overflow-hidden">
          {!tenants ? (
            <Spinner />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-hairline text-left text-xs text-muted">
                  <tr>
                    <th className="px-5 py-2 font-medium">Organisation</th>
                    <th className="px-5 py-2 font-medium">Users</th>
                    <th className="px-5 py-2 font-medium">Projects</th>
                    <th className="px-5 py-2 font-medium">Live</th>
                    <th className="px-5 py-2 font-medium">Builds 30d</th>
                    <th className="px-5 py-2 text-right font-medium">Credits</th>
                  </tr>
                </thead>
                <tbody>
                  {tenants.items.map((t) => (
                    <tr key={t.id} className="border-b border-hairline/60 last:border-0">
                      <td className="px-5 py-2">
                        {t.name}
                        {t.suspended && <Badge tone="clay">suspended</Badge>}
                        <span className="block text-2xs text-muted">joined {relative(t.created_at)}</span>
                      </td>
                      <td className="px-5 py-2 tabular-nums">{t.users}</td>
                      <td className="px-5 py-2 tabular-nums">{t.projects}</td>
                      <td className="px-5 py-2 tabular-nums">{t.live_apps}</td>
                      <td className="px-5 py-2 tabular-nums">{t.builds_30d}</td>
                      <td className="px-5 py-2 text-right tabular-nums">{credits(t.credits_balance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {tab === "builds" && (
        <Card className="overflow-hidden">
          {!builds ? (
            <Spinner />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-hairline text-left text-xs text-muted">
                  <tr>
                    <th className="px-5 py-2 font-medium">Build</th>
                    <th className="px-5 py-2 font-medium">Status</th>
                    <th className="px-5 py-2 font-medium">Files</th>
                    <th className="px-5 py-2 font-medium">Tokens</th>
                    <th className="px-5 py-2 font-medium">When</th>
                  </tr>
                </thead>
                <tbody>
                  {builds.items.map((b) => (
                    <tr key={b.id} className="border-b border-hairline/60 last:border-0">
                      <td className="px-5 py-2 font-mono text-2xs">{b.id.slice(0, 8)} · v{b.version}</td>
                      <td className="px-5 py-2"><Status value={b.status} /></td>
                      <td className="px-5 py-2 tabular-nums">
                        {b.files_written}
                        {b.ai_files > 0 && <span className="text-muted"> ({b.ai_files} ai)</span>}
                      </td>
                      <td className="px-5 py-2 tabular-nums text-muted">
                        {b.input_tokens + b.output_tokens || "—"}
                      </td>
                      <td className="px-5 py-2 text-xs text-muted">{relative(b.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
